/* cf-compare-drawer.js — reflet du store dans le tiroir persistant.

   **Ce fichier n'écrit jamais dans `localStorage`.** `<cf-compare-store>` est le
   seul auteur de l'état ; le tiroir s'abonne à `cf:compare:change`, reflète, et
   délègue tout retrait au store en décochant le contrôle correspondant. Une
   seule source d'écriture, un seul endroit à corriger.

   L'îlot JSON rendu par la section est un **cache de métadonnées, pas une
   autorité** : il ne couvre que 60 produits. Un handle absent y est affiché avec
   son seul handle en guise de libellé, jamais supprimé — un produit valide hors
   du sous-ensemble rendu ne doit pas disparaître de la sélection. Seule la
   matrice, qui interroge le serveur produit par produit, peut conclure à une
   péremption.

   Le tiroir n'est pas modal : il ne piège pas le focus et laisse la page
   utilisable derrière lui. Échap le referme.

   Une responsabilité, un fichier. */

/* D61 — plafond fixe du thème, 4 produits. Troisième et dernier littéral :
   le store plafonne à l'écriture, le tiroir replafonne à l'affichage. */
const CF_COMPARE_DRAWER_MAX = 4;

class CfCompareDrawer extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-cf-compare-list]');
    this.template = this.querySelector('[data-cf-compare-item]');
    this.hint = this.querySelector('[data-cf-compare-hint]');
    this.cta = this.querySelector('[data-cf-compare-cta]');
    this.live = this.querySelector('[data-cf-compare-live]');
    this.compareUrl = this.dataset.compareUrl || '';
    if (!this.list || !this.template || !this.cta) return;

    this.catalogue = this.readCatalogue();

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    document.addEventListener('cf:compare:change', this.onChange);
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);

    /* Le store publie à l'initialisation ; s'il a déjà publié avant que ce
       fichier ne soit chargé, on repart de l'état courant du DOM. */
    const store = document.querySelector('cf-compare-store');
    if (store && store.items) this.update(store.items);
  }

  disconnectedCallback() {
    document.removeEventListener('cf:compare:change', this.onChange);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  readCatalogue() {
    const node = this.querySelector('[data-cf-compare-catalogue]');
    const map = {};
    if (!node) return map;
    try {
      JSON.parse(node.textContent).forEach((entry) => {
        if (entry && entry.handle) map[entry.handle] = entry;
      });
    } catch (error) {
      /* Îlot illisible : le tiroir reste vide plutôt que d'afficher du faux. */
    }
    return map;
  }

  onChange(event) {
    this.update(event.detail ? event.detail.items : []);
  }

  update(items) {
    /* Un handle hors du cache reste affiché, libellé par son handle : le tiroir
       reflète l'état, il ne le juge pas. */
    const resolved = (items || [])
      .slice(0, CF_COMPARE_DRAWER_MAX)
      .map((handle) => this.catalogue[handle] || { handle: handle, title: handle });

    this.list.textContent = '';
    resolved.forEach((entry) => this.list.appendChild(this.buildItem(entry)));

    /* Zéro produit, ou tous périmés : le tiroir disparaît entièrement. */
    this.hidden = resolved.length === 0;

    /* Un seul produit : le tiroir reste, le CTA est inopérant et une invite
       explique quoi faire. Comparer un produit avec rien n'a pas de sens. */
    const ready = resolved.length > 1;
    this.hint.hidden = ready || resolved.length === 0;
    this.cta.setAttribute('aria-disabled', ready ? 'false' : 'true');
    this.cta.href = ready
      ? this.compareUrl + '?p=' + resolved.map((entry) => entry.handle).join(',')
      : this.compareUrl;
  }

  buildItem(entry) {
    const node = this.template.content.cloneNode(true);
    const image = node.querySelector('.cf-compare-drawer__image');
    const title = node.querySelector('.cf-compare-drawer__title');
    const button = node.querySelector('.cf-compare-drawer__remove');
    const label = node.querySelector('[data-cf-remove-label]');

    if (entry.image) image.src = entry.image;
    else image.remove();

    title.textContent = entry.title;
    button.dataset.cfCompareRemove = entry.handle;
    if (label) label.textContent = entry.title;

    return node;
  }

  onClick(event) {
    const close = event.target.closest('[data-cf-compare-close]');
    if (close) {
      this.hidden = true;
      return;
    }

    const remove = event.target.closest('[data-cf-compare-remove]');
    if (remove) {
      const handle = remove.dataset.cfCompareRemove;
      /* Délégation au store : on décoche le contrôle, le store fait le reste.
         Le tiroir ne touche pas à l'état. */
      const input = document.querySelector('[data-cf-compare][value="' + CSS.escape(handle) + '"]');
      if (input) {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const store = document.querySelector('cf-compare-store');
        if (store && store.remove) store.remove(handle);
      }
      this.announce(handle);
      return;
    }

    const cta = event.target.closest('[data-cf-compare-cta]');
    if (cta && cta.getAttribute('aria-disabled') === 'true') event.preventDefault();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && !this.hidden) this.hidden = true;
  }

  /* Le gabarit vient des locales : le script ne compose aucun texte. */
  announce(handle) {
    if (!this.live) return;
    const entry = this.catalogue[handle];
    const template = this.live.dataset.removeTemplate;
    if (!entry || !template) return;
    this.live.textContent = template.replace('%%title%%', entry.title);
  }
}

if (!customElements.get('cf-compare-drawer')) {
  customElements.define('cf-compare-drawer', CfCompareDrawer);
}
